import { ReactNode } from "react";
import { Logo } from "./Logo.tsx";
import { classNames } from "../Utils/classNames.ts";
import { AllFranchiseeScreenPath } from "../../Franchisee/FranchiseeScreen.tsx";
import {
  AcademicCapIcon,
  ArrowUpTrayIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  DocumentArrowDownIcon,
  GiftIcon,
  LightBulbIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  SignalIcon,
  SquaresPlusIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AllTopicScreenPath } from "../../Topic/TopicScreen.tsx";
import { AllUsersScreenPath } from "../../User/AllUserScreen.tsx";
import { AvailabilityImportScreenPath } from "../../Availability/AvailabilityImportScreen.tsx";
import { GenerateAvailabilityCsvScreenPath } from "../../Availability/GenerateAvailabilityCsvScreen.tsx";
import { SideMenuButton } from "./SideMenuButton.tsx";
import { useLocation } from "react-router-dom";
import { AvailabilityReportScreenPath } from "../../Availability/Report/AvailabilityReportScreen.tsx";
import { SpeakingLevelChangeSuggestionScreenPath } from "../../User/SpeakingLevelSuggestion/SpeakingLevelChangeSuggestionScreen.tsx";
import { AgGridClassRoomListPath } from "../../ClassRoom/AgGridClassRoomListScreen.tsx";
import { UsersEligibleForWarrantyScreenPath } from "../../User/Warranty/UsersEligibleForWarrantyScreen.tsx";
import { CheckClosersAvailabilityScreenPath } from "../../Closer/CheckClosersAvailabilityScreen.tsx";
import { AllUserGroupsScreenPath } from "../../UserGroup/AllUserGroupsScreen.tsx";
import { CoursePdfsScreenPath } from "../../CoursePdf/paths.ts";
import { IssuedGiftCardsScreenPath } from "../../IssuedGiftCard/IssuedGiftCardsScreen.tsx";
import { ReferredUsersScreenPath } from "../../Referral/ReferredUsersScreen.tsx";
import { UnredeemedPlansScreenPath } from "../../BulkClassRoom/UnredeemedPlansScreen.tsx";
import { OneOnOnePlanPurchasesScreenPath } from "../../OneOnOnePlanPurchase/OneOnOnePlanPurchasesScreen.tsx";
import { BulkClassRoomCreationRequestsScreenPath } from "../../BulkClassRoom/BulkClassRoomCreationRequestsScreen.tsx";
import { OnboardingLivesScreenPath } from "../../OnboardingLive/OnboardingLivePaths.ts";

type SideMenuEntry = {
  to: string;
  label: string;
  icon: ReactNode;
};

export function SideMenu({
  onNavigate,
  onClose,
  footer,
  collapsed = false,
  onToggleCollapsed,
}: {
  onNavigate?: () => void;
  onClose?: () => void;
  footer?: ReactNode;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  const location = useLocation();
  const entries: SideMenuEntry[] = [
    {
      to: AllFranchiseeScreenPath,
      label: "Teachers",
      icon: <AcademicCapIcon className={"h-5 w-5"} />,
    },
    {
      to: OnboardingLivesScreenPath,
      label: "Live onboarding",
      icon: <SignalIcon className={"h-5 w-5"} />,
    },
    {
      to: AllTopicScreenPath,
      label: "Topics",
      icon: <ChatBubbleLeftRightIcon className={"h-5 w-5"} />,
    },
    {
      to: CoursePdfsScreenPath,
      label: "Course PDFs",
      icon: <BookOpenIcon className={"h-5 w-5"} />,
    },
    {
      to: AllUsersScreenPath,
      label: "Users",
      icon: <UserIcon className={"h-5 w-5"} />,
    },
    {
      to: AllUserGroupsScreenPath,
      label: "User Groups",
      icon: <UserGroupIcon className={"h-5 w-5"} />,
    },
    {
      to: AgGridClassRoomListPath,
      label: "Classrooms",
      icon: <VideoCameraIcon className={"h-5 w-5"} />,
    },
    {
      to: UnredeemedPlansScreenPath,
      label: "Create Bulk classes",
      icon: <SquaresPlusIcon className={"h-5 w-5"} />,
    },
    {
      to: BulkClassRoomCreationRequestsScreenPath,
      label: "Bulk classes created",
      icon: <RectangleStackIcon className={"h-5 w-5"} />,
    },
    {
      to: OneOnOnePlanPurchasesScreenPath,
      label: "1 on 1 Plan Purchases",
      icon: <CreditCardIcon className={"h-5 w-5"} />,
    },
    {
      to: AvailabilityImportScreenPath,
      label: "Import availability",
      icon: <ArrowUpTrayIcon className={"h-5 w-5"} />,
    },
    {
      to: GenerateAvailabilityCsvScreenPath,
      label: "Generate Availability CSV",
      icon: <DocumentArrowDownIcon className={"h-5 w-5"} />,
    },
    {
      to: AvailabilityReportScreenPath,
      label: "Availability Report",
      icon: <ChartBarSquareIcon className={"h-5 w-5"} />,
    },
    {
      to: SpeakingLevelChangeSuggestionScreenPath,
      label: "Speaking Level Suggestions",
      icon: <LightBulbIcon className={"h-5 w-5"} />,
    },
    {
      to: UsersEligibleForWarrantyScreenPath,
      label: "Users eligible for warranty",
      icon: <ShieldCheckIcon className={"h-5 w-5"} />,
    },
    {
      to: CheckClosersAvailabilityScreenPath,
      label: "Check closers availability",
      icon: <CalendarDaysIcon className={"h-5 w-5"} />,
    },
    {
      to: IssuedGiftCardsScreenPath,
      label: "Issued Gift Card",
      icon: <GiftIcon className={"h-5 w-5"} />,
    },
    {
      to: ReferredUsersScreenPath,
      label: "Referred Users",
      icon: <UserPlusIcon className={"h-5 w-5"} />,
    },
  ];

  return (
    <div
      className={classNames(
        "shadow-lg border-r shadow-slate-200/10 border-slate-200 flex flex-col h-full bg-white",
        collapsed ? "p-3" : "p-4",
      )}
    >
      <div
        className={classNames(
          "flex items-center gap-4",
          collapsed ? "justify-center" : "justify-between",
        )}
      >
        {!collapsed && <Logo />}
        {onToggleCollapsed && (
          <button
            type={"button"}
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            className={"p-2 rounded-lg text-slate-500 hover:bg-slate-100"}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className={"h-5 w-5"} />
            ) : (
              <ChevronDoubleLeftIcon className={"h-5 w-5"} />
            )}
          </button>
        )}
        {onClose && (
          <button
            type={"button"}
            onClick={onClose}
            aria-label={"Close menu"}
            className={"p-2 -mr-2 rounded-lg hover:bg-slate-100"}
          >
            <XMarkIcon className={"h-6 w-6"} />
          </button>
        )}
      </div>

      <div
        className={classNames(
          "mt-4 w-full flex flex-col overflow-y-auto flex-1",
          collapsed ? "items-center space-y-2" : "space-y-3",
        )}
      >
        {entries.map((entry) => (
          <SideMenuButton
            key={entry.to}
            to={entry.to}
            label={entry.label}
            icon={entry.icon}
            isActive={location.pathname === entry.to}
            onNavigate={onNavigate}
            collapsed={collapsed}
          />
        ))}
      </div>
      {footer && (
        <div className={"mt-4 pt-4 border-t border-slate-200"}>{footer}</div>
      )}
    </div>
  );
}
